/**
 * Settings store using Zustand.
 * Manages settings modal state and provides methods for updating user settings.
 */
import { create } from 'zustand';
import { settingsApi, SettingsUpdateRequest } from '../api/settings';
import { useAuthStore } from './authStore';
import { setTheme, setFont } from '../utils/theme';

type SettingsTab = 'profile' | 'theological' | 'api-keys' | 'appearance' | 'notifications' | 'organization' | 'account';

interface SettingsState {
  // Modal state
  isOpen: boolean;
  activeTab: SettingsTab;
  hasUnsavedChanges: boolean;

  // Actions
  openSettings: (tab?: SettingsTab) => void;
  closeSettings: () => void;
  setActiveTab: (tab: SettingsTab) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;

  // Settings updates
  updateSettings: (settings: SettingsUpdateRequest) => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  isOpen: false,
  activeTab: 'profile',
  hasUnsavedChanges: false,

  openSettings: (tab = 'profile') => {
    set({ isOpen: true, activeTab: tab, hasUnsavedChanges: false });
  },

  closeSettings: () => {
    const { hasUnsavedChanges } = get();

    // Warn if there are unsaved changes
    if (hasUnsavedChanges) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmClose) return;

      // Revert theme and font to saved values on discard
      const user = useAuthStore.getState().user;
      setTheme(user?.color_mode || 'dark');
      setFont(user?.chat_font || 'system');
    }

    set({ isOpen: false, hasUnsavedChanges: false });
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  setHasUnsavedChanges: (hasChanges) => {
    set({ hasUnsavedChanges: hasChanges });
  },

  updateSettings: async (settings) => {
    try {
      // Update settings via API
      const updatedUser = await settingsApi.updateSettings(settings);

      // Update auth store with new user data
      useAuthStore.getState().setUser(updatedUser);

      // Clear unsaved changes flag
      set({ hasUnsavedChanges: false });

    } catch (error: any) {
      console.error('Failed to update settings:', error);
      throw new Error(
        error.response?.data?.detail?.message || 'Failed to update settings'
      );
    }
  },
}));
